import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import { consoleApi, CONSOLE_API_BASE } from "@/lib/console-auth";
import "@/styles/console.css";

/* ─────────────────────────────────────────────────────────────────────────────
   CsvMigration — explicit, production-safe data-portability surface.

   • Exports the live MongoDB rows as CSV (tenants OR listings).
   • Imports a CSV with dry-run preview, then commits idempotently.
   • Both operations are key-stable (tenant_id for tenants, _lookup_key for
     listings) so redeploying the app never touches existing rows.
   ───────────────────────────────────────────────────────────────────────── */

export default function CsvMigration() {
    const [kind, setKind] = useState("tenants");

    return (
        <div data-testid="csv-migration">
            {/* Production-safety banner */}
            <div className="console-restricted-banner mb-6" data-testid="csv-safety-banner">
                <AlertTriangle size={18} className="shrink-0 mt-[2px]" />
                <div>
                    <p className="font-semibold mb-1" style={{ letterSpacing: "0.04em" }}>
                        Production redeploy safety
                    </p>
                    <p style={{ color: "rgba(255, 214, 107, 0.78)", fontSize: 12, lineHeight: 1.6 }}>
                        All data lives in MongoDB and persists across deploys. Startup seeds are idempotent —
                        they only insert if a row is missing, never overwrite. Imports are upserts by
                        stable key (<code>tenant_id</code> · <code>_lookup_key</code>) so re-importing the
                        same CSV is harmless.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 mb-6" data-testid="csv-kind-tabs">
                {[
                    { key: "tenants",  label: "Tenants",  hint: "All tenant + tier configuration" },
                    { key: "listings", label: "Listings", hint: "Clubs · hotels · venues (lc_listings)" },
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setKind(t.key)}
                        className={`console-tile ${kind === t.key ? "console-tile-active" : ""}`}
                        style={{ width: "auto", minWidth: 220 }}
                        data-testid={`csv-tab-${t.key}`}
                    >
                        <FileText size={14} style={{ color: "var(--console-brass)" }} />
                        <div className="text-left">
                            <p>{t.label}</p>
                            <p className="console-eyebrow" style={{ marginTop: 2 }}>{t.hint}</p>
                        </div>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ExportPanel kind={kind} />
                <ImportPanel kind={kind} />
            </div>
        </div>
    );
}

function ExportPanel({ kind }) {
    const [country, setCountry] = useState("");
    const [limit, setLimit] = useState(5000);

    const onExport = async () => {
        const token = localStorage.getItem("lc_console_token");
        const params = new URLSearchParams();
        if (kind === "listings" && country) params.append("country", country);
        if (kind === "listings") params.append("limit", String(limit));
        const url = `${CONSOLE_API_BASE}/console/export/${kind}.csv${params.toString() ? "?" + params : ""}`;
        // Use fetch so we can attach the bearer header (browser's native download
        // won't include Authorization headers).
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) {
            alert(`Export failed (${r.status})`);
            return;
        }
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = kind === "listings" && country
            ? `lc_listings_${country.toLowerCase()}.csv`
            : `lc_${kind}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <div className="console-card p-6" data-testid={`export-panel-${kind}`}>
            <div className="flex items-center gap-3 mb-4">
                <Download size={18} style={{ color: "var(--console-brass)" }} />
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: 18 }}>
                    Export {kind}
                </h3>
            </div>

            <p style={{ color: "var(--console-text-muted)", fontSize: 13, lineHeight: 1.6 }}>
                Streams the current MongoDB state as a CSV file. Use this before any major release as a
                backup, or to hand off the archive to a partner.
            </p>

            {kind === "listings" && (
                <>
                    <div className="mt-5">
                        <label className="console-eyebrow block mb-2">Country (optional)</label>
                        <input
                            className="console-input"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            placeholder="leave blank for all countries"
                            data-testid="export-listings-country"
                        />
                    </div>
                    <div className="mt-4">
                        <label className="console-eyebrow block mb-2">Limit</label>
                        <input
                            type="number"
                            className="console-input"
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value) || 1000)}
                            min={1}
                            max={50000}
                            data-testid="export-listings-limit"
                        />
                    </div>
                </>
            )}

            <button
                onClick={onExport}
                className="console-btn-primary mt-6"
                data-testid={`export-btn-${kind}`}
            >
                <Download size={14} className="inline mr-2" /> Download CSV
            </button>
        </div>
    );
}

function ImportPanel({ kind }) {
    const inputRef = useRef(null);
    const [csvText, setCsvText] = useState("");
    const [report, setReport] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setReport(null);
        setCsvText("");
        setError("");
    }, [kind]);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const text = await f.text();
        setCsvText(text);
        setReport(null);
        setError("");
    };

    const submit = async (dry_run) => {
        if (!csvText.trim()) {
            setError("Pick a CSV first.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            const { data } = await consoleApi.post(`/console/import/${kind}`, {
                csv: csvText, dry_run,
            });
            setReport(data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="console-card p-6" data-testid={`import-panel-${kind}`}>
            <div className="flex items-center gap-3 mb-4">
                <Upload size={18} style={{ color: "var(--console-brass)" }} />
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: 18 }}>
                    Import {kind}
                </h3>
            </div>

            <p style={{ color: "var(--console-text-muted)", fontSize: 13, lineHeight: 1.6 }}>
                Idempotent — existing rows are merged (by{" "}
                <code>{kind === "tenants" ? "tenant_id" : "_lookup_key"}</code>),
                new rows are inserted. Re-uploading the same CSV is harmless.
            </p>

            <div className="mt-5">
                <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={onFile}
                    className="console-input"
                    data-testid={`import-file-${kind}`}
                />
                {csvText && (
                    <p className="console-eyebrow mt-2">
                        {csvText.split("\n").length - 1} rows · {(csvText.length / 1024).toFixed(1)} KB
                    </p>
                )}
            </div>

            {error && (
                <p style={{ color: "var(--console-danger)" }} className="mt-3 text-sm" data-testid="import-error">{error}</p>
            )}

            <div className="mt-5 flex gap-3">
                <button
                    onClick={() => submit(true)}
                    disabled={submitting || !csvText}
                    className="console-btn-ghost"
                    data-testid={`import-dryrun-${kind}`}
                >
                    Preview (dry-run)
                </button>
                <button
                    onClick={() => submit(false)}
                    disabled={submitting || !csvText}
                    className="console-btn-primary"
                    data-testid={`import-commit-${kind}`}
                >
                    {submitting ? "Working…" : "Commit"}
                </button>
            </div>

            {report && (
                <div className="mt-6 p-4" style={{ background: "rgba(74, 222, 128, 0.06)", borderRadius: 4 }} data-testid="import-report">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 size={16} style={{ color: "var(--console-success)" }} />
                        <p className="font-semibold">{report.dry_run ? "Dry run report" : "Import complete"}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-sm">
                        <Stat label="Rows"     v={report.rows} />
                        <Stat label="Inserted" v={report.inserted} />
                        <Stat label="Updated"  v={report.updated} />
                        <Stat label="Errors"   v={(report.errors || []).length} />
                    </div>
                    {(report.errors || []).length > 0 && (
                        <details className="mt-4">
                            <summary style={{ cursor: "pointer", color: "var(--console-danger)", fontSize: 13 }}>
                                Show {report.errors.length} errors
                            </summary>
                            <pre style={{ fontSize: 11, marginTop: 8, maxHeight: 200, overflow: "auto", color: "var(--console-text-muted)" }}>
                                {JSON.stringify(report.errors, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ label, v }) {
    return (
        <div>
            <p className="console-eyebrow">{label}</p>
            <p style={{ fontSize: 22, fontFamily: "Georgia, serif", color: "var(--console-brass-bright)" }}>{v}</p>
        </div>
    );
}
